import { create } from 'zustand';
import { adminSyncRequestsApi } from '@api/api-config.ts';
import {
  AdminPriceAuctionFixSummary,
  AdminSyncRequestListResponse,
  AdminSyncRequestVerifyResponse,
  AdminSyncBatchOperationResult,
  AdminSyncBatchSummary,
  AdminSyncControlStatus,
  AdminSyncExecuteResult,
  AdminSyncFailureDetail,
  AdminSyncRequestJsonResponse,
  SyncFailureMode,
} from '@api/generated/portal-be-admin';

export interface SyncRequestFilters {
  syncType?: string;
  actionType?: string;
  status?: string[];
  jsonSearch?: string;
  requestId?: string;
  hideIgnored?: boolean;
  author?: string;
  page: number;
  size: number;
  sortBy?: 'PROCESSING_ORDER' | 'ID';
  sortDir?: 'ASC' | 'DESC';
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
}

interface AdminSyncRequestStoreState {
  loading: boolean;
  error: string | null;
  data: AdminSyncRequestListResponse[] | null;
  pagination: PaginationInfo | null;
  control: AdminSyncControlStatus | null;
  /** Souhrn dávky — plněn jen když je aktivní filtr na requestId. */
  batch: AdminSyncBatchSummary | null;
  batchLoading: boolean;
  loadSyncRequests: (filters: SyncRequestFilters) => Promise<void>;
  loadControl: () => Promise<void>;
  loadBatch: (requestId?: string) => Promise<void>;
  start: () => Promise<AdminSyncControlStatus>;
  stop: () => Promise<AdminSyncControlStatus>;
  setFailureMode: (failureMode: SyncFailureMode) => Promise<AdminSyncControlStatus>;
  setIgnored: (id: number, ignored: boolean) => Promise<void>;
  setBatchIgnored: (requestId: string, ignored: boolean) => Promise<AdminSyncBatchOperationResult>;
  deleteBatch: (requestId: string) => Promise<AdminSyncBatchOperationResult>;
  execute: (id: number) => Promise<AdminSyncExecuteResult>;
  verify: (id: number) => Promise<AdminSyncRequestVerifyResponse>;
  loadFailure: (id: number) => Promise<AdminSyncFailureDetail>;
  loadJson: (id: number) => Promise<AdminSyncRequestJsonResponse>;
  findPriceAuctionFix: () => Promise<AdminPriceAuctionFixSummary>;
  applyPriceAuctionFix: () => Promise<AdminPriceAuctionFixSummary>;
}

export const useAdminSyncRequestStore = create<AdminSyncRequestStoreState>(set => ({
  loading: false,
  error: null,
  data: null,
  pagination: null,
  control: null,
  batch: null,
  batchLoading: false,

  loadSyncRequests: async (filters: SyncRequestFilters) => {
    try {
      set({ loading: true, error: null });
      const response = await adminSyncRequestsApi.getAdminSyncRequests({
        page: filters.page,
        size: filters.size,
        sortBy: filters.sortBy as any,
        sortDir: filters.sortDir as any,
        syncType: filters.syncType as any,
        actionType: filters.actionType as any,
        status: (filters.status && filters.status.length > 0 ? filters.status : undefined) as any,
        jsonSearch: filters.jsonSearch?.trim() ? filters.jsonSearch.trim() : undefined,
        requestId: filters.requestId || undefined,
        hideIgnored: filters.hideIgnored || undefined,
        author: filters.author?.trim() ? filters.author.trim() : undefined,
      });
      const result = response.data;
      set({
        loading: false,
        error: null,
        data: result.items || [],
        pagination: {
          page: result.page,
          size: result.size,
          total: result.total,
          totalPages: result.totalPages,
          hasPrev: result.hasPrev,
          hasNext: result.hasNext,
        },
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba pri nacitani sync requestu',
      });
    }
  },

  loadControl: async () => {
    const response = await adminSyncRequestsApi.getControl();
    set({ control: response.data });
  },

  start: async () => {
    const response = await adminSyncRequestsApi.startControl();
    set({ control: response.data });
    return response.data;
  },

  stop: async () => {
    const response = await adminSyncRequestsApi.stopControl();
    set({ control: response.data });
    return response.data;
  },

  setFailureMode: async (failureMode: SyncFailureMode) => {
    const response = await adminSyncRequestsApi.setAdminSyncFailureMode({
      adminSetFailureModeRequest: { failureMode },
    });
    set({ control: response.data });
    return response.data;
  },

  loadBatch: async (requestId?: string) => {
    if (!requestId) {
      set({ batch: null, batchLoading: false });
      return;
    }
    try {
      set({ batchLoading: true });
      const response = await adminSyncRequestsApi.getAdminSyncBatch({ requestId });
      set({ batch: response.data, batchLoading: false });
    } catch {
      // Neexistující dávka není chyba stránky — panel se prostě nezobrazí.
      set({ batch: null, batchLoading: false });
    }
  },

  setIgnored: async (id: number, ignored: boolean) => {
    await adminSyncRequestsApi.setIgnored({ id, adminSetIgnoredRequest: { ignored } });
  },

  setBatchIgnored: async (requestId: string, ignored: boolean) => {
    const response = await adminSyncRequestsApi.setAdminSyncBatchIgnored({
      requestId,
      adminSetBatchIgnoredRequest: { ignored },
    });
    set({ control: response.data.control });
    return response.data;
  },

  deleteBatch: async (requestId: string) => {
    const response = await adminSyncRequestsApi.deleteAdminSyncBatch({ requestId });
    set({ control: response.data.control, batch: null });
    return response.data;
  },

  execute: async (id: number) => {
    const response = await adminSyncRequestsApi.executeSyncRequest({ id });
    return response.data;
  },

  verify: async (id: number) => {
    const response = await adminSyncRequestsApi.verifySyncRequest({ id });
    return response.data;
  },

  loadFailure: async (id: number) => {
    const response = await adminSyncRequestsApi.getFailure({ id });
    return response.data;
  },

  loadJson: async (id: number) => {
    const response = await adminSyncRequestsApi.getSyncRequestJson({ id });
    return response.data;
  },

  findPriceAuctionFix: async () => {
    const response = await adminSyncRequestsApi.findAdminPricesWithoutAuction();
    return response.data;
  },

  applyPriceAuctionFix: async () => {
    const response = await adminSyncRequestsApi.fixAdminPricesWithoutAuction();
    return response.data;
  },
}));
